import {
  ArrowLeftIcon,
  PlusIcon,
  SearchIcon,
  Settings2Icon,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import useSWR, { type SWRResponse } from 'swr'

import { DesktopNavigation, MobileNavigation } from './Navigation'
import { Sheet } from './Sheet'
import { Link } from '../lib/Link'
import { needsHousehold, useMe } from '../lib/auth-client'
import { useRouter, useSearchParams } from '../lib/navigation'
import { api } from '../lib/password'
import { fetcher } from '../lib/swr'
import PlanDetail from '../pg/PlanDetail'
import {
  isOrphanListId,
  type FullList,
  type FullOrder,
  type FullPlan,
  type FullRecipe,
} from '../pg/types'

type PublicRecipe = FullRecipe & {
  lastConsumedAt?: string | null
  popularity?: number
  listCount?: number
  planCount?: number
  ingredientCount?: number
}

/** List-centric cookbook; own-tenant orphans appear under synthetic "Zonder lijst". */
export function Cookbook({ publicMode = false }: { publicMode?: boolean }) {
  const router = useRouter()
  const search = useSearchParams()
  const recipeId = Number(search.get('recipeId') || 0) || null
  const listId = Number(search.get('list') || 0) || null
  const startEditing = search.get('edit') === '1'
  const { data: me } = useMe()
  const tenantId = me?.user?.tenantId ?? null
  const signedIn = Boolean(tenantId)
  const base = publicMode ? '/' : '/recipes'

  useEffect(() => {
    if (needsHousehold(me)) router.replace('/onboarding')
  }, [me, router])

  const lists = useSWR<FullList[]>('/api/public/lists', fetcher)
  const orders = useSWR<FullOrder[]>(signedIn ? '/api/orders' : null, fetcher)
  const home = useSWR<FullPlan[]>(
    signedIn ? '/api/plans?view=home' : null,
    fetcher
  )

  const [term, setTerm] = useState('')
  const [isEditing, setIsEditing] = useState(startEditing)

  useEffect(() => {
    setIsEditing(startEditing)
  }, [recipeId, startEditing])

  const sortedLists = useMemo(() => lists.data || [], [lists.data])

  const myLists = useMemo(
    () =>
      tenantId == null
        ? []
        : sortedLists.filter(
            (l) => l.tenantId === tenantId && !isOrphanListId(l.id)
          ),
    [sortedLists, tenantId]
  )

  const activeList = sortedLists.find((l) => l.id === listId) ?? null

  const listRecipes = useMemo(() => {
    if (!activeList) return [] as PublicRecipe[]
    let rows = activeList.recipes as PublicRecipe[]
    const q = term.trim().toLowerCase()
    if (q) {
      rows = rows.filter((r) => {
        const hay =
          `${r.display || ''} ${r.main || ''} ${r.course || ''} ` +
          (r.ingredients || []).map((i) => i.product.display).join(' ')
        return hay.toLowerCase().includes(q)
      })
    }
    return rows
  }, [activeList, term])

  const filteredLists = useMemo(() => {
    const withRecipes = sortedLists.filter((list) => list.recipes.length > 0)
    const q = term.trim().toLowerCase()
    if (!q) return withRecipes
    return withRecipes.filter((list) => {
      if ((list.display || '').toLowerCase().includes(q)) return true
      return list.recipes.some((r) => {
        const hay =
          `${r.display || ''} ${r.main || ''} ${r.course || ''} ` +
          (r.ingredients || []).map((i) => i.product.display).join(' ')
        return hay.toLowerCase().includes(q)
      })
    })
  }, [sortedLists, term])

  function cookbookUrl(opts: {
    recipeId?: number | null
    listId?: number | null
    edit?: boolean | null
  }) {
    const params = new URLSearchParams()
    const nextList = opts.listId === undefined ? listId : opts.listId
    const nextRecipe = opts.recipeId === undefined ? recipeId : opts.recipeId
    const nextEdit = opts.edit === undefined ? startEditing : opts.edit
    if (nextList) params.set('list', String(nextList))
    if (nextRecipe) params.set('recipeId', String(nextRecipe))
    if (nextEdit && nextRecipe) params.set('edit', '1')
    const qs = params.toString()
    return qs ? `${base}?${qs}` : base
  }

  function openRecipe(id: number, edit = false) {
    router.push(cookbookUrl({ recipeId: id, edit }))
  }

  function closeRecipe() {
    router.replace(cookbookUrl({ recipeId: null, edit: false }))
  }

  function openList(id: number) {
    router.push(cookbookUrl({ listId: id, recipeId: null, edit: false }))
  }

  function closeList() {
    router.push(cookbookUrl({ listId: null, recipeId: null, edit: false }))
  }

  useEffect(() => {
    if (!listId || lists.isLoading || !lists.data) return
    const list = lists.data.find((l) => l.id === listId)
    if (!list || list.recipes.length === 0) {
      router.replace(cookbookUrl({ listId: null }))
    }
  }, [listId, lists.data, lists.isLoading, router])

  async function createRecipe() {
    if (!activeList || activeList.tenantId !== tenantId) {
      toast.error('Open eerst een van jouw lijsten om een recept toe te voegen')
      return
    }
    try {
      const { data } = await api.post<FullRecipe>('/api/recipes', {
        display: 'Nieuw recept',
      })
      if (!isOrphanListId(activeList.id)) {
        await fetch(`/api/lists/${activeList.id}/recipes`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ recipeId: data.id }),
        })
      }
      await lists.mutate()
      openRecipe(data.id, true)
    } catch (e: unknown) {
      const message =
        (e as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || 'Aanmaken mislukt'
      toast.error(message)
    }
  }

  const selected =
    (recipeId &&
      sortedLists.flatMap((l) => l.recipes).find((r) => r.id === recipeId)) ||
    undefined

  return (
    <Sheet
      open={Boolean(recipeId)}
      onClose={closeRecipe}
      wide
      sheet={
        recipeId ? (
          <>
            <PlanDetail
              key={`${recipeId}-${startEditing ? 'edit' : 'view'}`}
              recipeId={recipeId}
              recipe={selected}
              initialEditing={startEditing}
              onChange={() => {
                lists.mutate()
              }}
              onClose={closeRecipe}
              onRecipeIdChange={(id) => {
                router.replace(cookbookUrl({ recipeId: id, edit: true }))
              }}
              onEditingChange={(editing) => {
                setIsEditing(editing)
                if (!editing && startEditing) {
                  router.replace(cookbookUrl({ recipeId, edit: false }))
                }
              }}
            />

            {signedIn && !isEditing ? (
              <>
                <AddToOrder recipeId={recipeId} home={home} orders={orders} />
                <AddToList
                  recipeId={recipeId}
                  lists={myLists}
                  onChange={() => {
                    lists.mutate()
                  }}
                />
              </>
            ) : null}
          </>
        ) : null
      }
    >
      <main className="min-h-screen flex flex-col text-white">
        <DesktopNavigation />

        <div className="flex-1 w-full max-w-5xl mx-auto px-4 pt-4 pb-28 md:px-6 md:pt-6 md:pb-16">
          <div className="sticky top-0 sticky-below-nav z-10 -mx-4 px-4 py-3 md:mx-0 md:px-0 bg-bg/90 backdrop-blur-md mb-4">
            <label className="relative block">
              <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/45 pointer-events-none" />
              <input
                type="search"
                placeholder={
                  activeList
                    ? 'Zoeken in deze lijst…'
                    : 'Zoeken op lijst of recept…'
                }
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                className="w-full rounded-2xl bg-white/10 hover:bg-white/15 focus:bg-white/15 border border-white/10 focus:border-white/25 pl-12 pr-4 py-3.5 text-white placeholder:text-white/40 outline-none transition-colors"
              />
            </label>

            <div className="flex items-center gap-2 mt-3">
              {activeList ? (
                <button
                  type="button"
                  onClick={closeList}
                  className="shrink-0 rounded-full text-sm px-3 py-2 font-medium bg-white/10 text-white hover:bg-white/20 transition-colors inline-flex items-center gap-1.5"
                >
                  <ArrowLeftIcon className="w-4 h-4" />
                  Lijsten
                </button>
              ) : null}
              <div className="min-w-0 flex-1">
                {activeList ? (
                  <h1 className="text-lg md:text-xl font-semibold truncate">
                    {activeList.display}
                    {activeList.tenantId !== tenantId &&
                    activeList.tenantName ? (
                      <span className="ml-2 text-sm font-normal text-white/45 inline-flex items-center gap-1.5 align-middle">
                        {activeList.tenantHasImage ? (
                          <img
                            src={`/api/tenants/${activeList.tenantId}/image`}
                            alt=""
                            className="size-4 rounded-full object-cover ring-1 ring-white/20"
                          />
                        ) : null}
                        {activeList.tenantName}
                      </span>
                    ) : null}
                  </h1>
                ) : (
                  <h1 className="text-lg md:text-xl font-semibold">Kookboek</h1>
                )}
              </div>
              {signedIn ? (
                <div className="flex shrink-0 gap-1.5">
                  {activeList && activeList.tenantId === tenantId ? (
                    <button
                      type="button"
                      onClick={createRecipe}
                      className="rounded-full text-sm px-3 py-2 font-medium bg-white text-green-950 hover:bg-white/90 transition-colors inline-flex items-center gap-1.5"
                    >
                      <PlusIcon className="w-4 h-4" />
                      Nieuw
                    </button>
                  ) : null}
                  <Link
                    href="/settings/lists"
                    className="rounded-full text-sm px-3 py-2 font-medium bg-transparent text-white/55 hover:text-white hover:bg-white/10 transition-colors inline-flex items-center gap-1.5"
                    title="Lijsten beheren"
                  >
                    <Settings2Icon className="w-4 h-4" />
                  </Link>
                </div>
              ) : null}
            </div>
          </div>

          {activeList ? (
            <RecipeGrid
              recipes={listRecipes}
              loading={lists.isLoading}
              error={Boolean(lists.error)}
              selectedId={recipeId}
              viewerTenantId={tenantId}
              listTenantName={activeList.tenantName}
              onSelect={openRecipe}
            />
          ) : (
            <ListGrid
              lists={filteredLists}
              loading={lists.isLoading}
              error={Boolean(lists.error)}
              viewerTenantId={tenantId}
              onSelect={openList}
            />
          )}

          {!signedIn ? (
            <p className="mt-10 text-sm text-white/50">
              <Link href="/start" className="underline hover:text-white">
                Start een huishouden
              </Link>
              {' · '}
              <Link href="/login" className="underline hover:text-white">
                Inloggen
              </Link>
            </p>
          ) : null}
        </div>

        <MobileNavigation />
      </main>
    </Sheet>
  )
}

function ListCoverMedia({
  cover,
}: {
  cover: { imageSrc: string | null; label: string; fromRecipe?: boolean }
}) {
  const [failed, setFailed] = useState(false)
  if (!cover.imageSrc || failed) {
    return (
      <div className="absolute inset-0 flex items-end p-3 md:p-4">
        <span className="text-sm md:text-base font-medium text-white/70 line-clamp-3 leading-snug">
          {cover.label}
        </span>
      </div>
    )
  }
  return (
    <img
      src={cover.imageSrc}
      alt=""
      className="absolute inset-0 h-full w-full object-cover opacity-90 group-hover:scale-[1.03] transition-transform duration-500"
      onError={() => setFailed(true)}
    />
  )
}

function listCover(list: FullList): {
  imageSrc: string | null
  label: string
  fromRecipe?: boolean
} {
  if (list.hasImage) {
    return {
      imageSrc: `/api/lists/${list.id}/image`,
      label: list.display,
    }
  }
  const first = list.recipes[0]
  if (first) {
    return {
      imageSrc: `/api/recipes/${first.id}/image`,
      label: first.display || list.display,
      fromRecipe: true,
    }
  }
  return {
    imageSrc: null,
    label: list.display,
  }
}

function ListGrid({
  lists,
  loading,
  error,
  viewerTenantId,
  onSelect,
}: {
  lists: FullList[]
  loading: boolean
  error: boolean
  viewerTenantId: number | null
  onSelect: (id: number) => void
}) {
  if (loading && lists.length === 0) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 md:gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="aspect-4/3 rounded-2xl bg-white/5 animate-pulse"
            style={{ animationDelay: `${i * 40}ms` }}
          />
        ))}
      </div>
    )
  }

  if (error) {
    return <p className="text-red-200 py-6">Kon lijsten niet laden</p>
  }

  if (lists.length === 0) {
    return (
      <p className="text-white/45 py-6">
        Nog geen lijsten. Open een recept en voeg het toe aan een nieuwe lijst,
        of volg een huishouden.
      </p>
    )
  }

  return (
    <ul className="grid grid-cols-2 sm:grid-cols-3 gap-3 md:gap-4">
      {lists.map((list, i) => {
        const cover = listCover(list)
        const mine = viewerTenantId != null && list.tenantId === viewerTenantId
        return (
          <li
            key={list.id}
            className="animate-[cookbook-row_0.4s_ease-out_both]"
            style={{ animationDelay: `${Math.min(i, 20) * 18}ms` }}
          >
            <button
              type="button"
              onClick={() => onSelect(list.id)}
              className="group w-full text-left rounded-2xl overflow-hidden bg-white/8 hover:bg-white/12 ring-1 ring-white/10 hover:ring-white/20 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-white/40"
            >
              <div className="relative aspect-4/3 overflow-hidden bg-linear-to-br from-emerald-900/80 via-green-950 to-stone-950">
                <ListCoverMedia cover={cover} />
                <div className="absolute inset-x-0 bottom-0 h-1/2 bg-linear-to-t from-black/55 to-transparent pointer-events-none" />
              </div>
              <div className="px-3 py-2.5">
                <div className="font-medium text-[15px] leading-snug truncate">
                  {list.display}
                </div>
                <div className="mt-0.5 text-xs text-white/45 flex items-center gap-1.5">
                  <span className="tabular-nums">
                    {list.recipes.length} recept
                    {list.recipes.length === 1 ? '' : 'en'}
                  </span>
                  {!mine && list.tenantName ? (
                    <>
                      <span>·</span>
                      <span className="truncate inline-flex items-center gap-1 min-w-0">
                        {list.tenantHasImage ? (
                          <img
                            src={`/api/tenants/${list.tenantId}/image`}
                            alt=""
                            className="size-3.5 rounded-full object-cover shrink-0 ring-1 ring-white/20"
                          />
                        ) : null}
                        <span className="truncate">{list.tenantName}</span>
                      </span>
                    </>
                  ) : null}
                </div>
              </div>
            </button>
          </li>
        )
      })}
    </ul>
  )
}

function RecipeGrid({
  recipes,
  loading,
  error,
  selectedId,
  viewerTenantId,
  listTenantName,
  onSelect,
}: {
  recipes: PublicRecipe[]
  loading: boolean
  error: boolean
  selectedId: number | null
  viewerTenantId: number | null
  listTenantName?: string | null
  onSelect: (id: number) => void
}) {
  if (loading && recipes.length === 0) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1">
        {Array.from({ length: 8 }).map((_, i) => (
          <div
            key={i}
            className="h-14 rounded-xl bg-white/5 animate-pulse"
            style={{ animationDelay: `${i * 40}ms` }}
          />
        ))}
      </div>
    )
  }

  if (error) {
    return <p className="text-red-200 py-6">Kon recepten niet laden</p>
  }

  if (recipes.length === 0) {
    return <p className="text-white/45 py-6">Geen recepten in deze lijst</p>
  }

  return (
    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 md:gap-x-10">
      {recipes.map((recipe, i) => {
        const ingredients =
          recipe.ingredientCount ?? recipe.ingredients?.length ?? 0
        const selected = recipe.id === selectedId
        const other =
          viewerTenantId != null && recipe.tenantId !== viewerTenantId
        const otherLabel = recipe.tenantName || listTenantName
        return (
          <li
            key={recipe.id}
            className="animate-[cookbook-row_0.4s_ease-out_both]"
            style={{ animationDelay: `${Math.min(i, 20) * 18}ms` }}
          >
            <button
              type="button"
              onClick={() => onSelect(recipe.id)}
              className={
                (selected ? 'bg-white/15 ' : 'hover:bg-white/10 ') +
                'group w-full text-left rounded-xl px-3 py-3.5 -mx-1 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-white/40'
              }
            >
              <span className="flex items-start gap-2">
                <span className="font-medium text-[15px] md:text-base leading-snug group-hover:underline decoration-white/40 underline-offset-2 min-w-0">
                  {recipe.display}
                </span>
                {other ? (
                  <span className="shrink-0 mt-0.5 inline-flex items-center gap-1 rounded-md bg-amber-400/15 text-amber-100/90 ring-1 ring-amber-200/20 px-1.5 py-0.5 text-[10px] font-medium tracking-wide">
                    {recipe.tenantHasImage ? (
                      <img
                        src={`/api/tenants/${recipe.tenantId}/image`}
                        alt=""
                        className="size-3.5 rounded-full object-cover ring-1 ring-amber-200/30"
                      />
                    ) : null}
                    {otherLabel || 'Ander huishouden'}
                  </span>
                ) : null}
              </span>
              <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-white/45">
                {recipe.course ? <span>{recipe.course}</span> : null}
                {recipe.course && ingredients > 0 ? <span>·</span> : null}
                {ingredients > 0 ? (
                  <span>{ingredients} ingredienten</span>
                ) : (
                  <span className="text-white/30">geen ingredienten</span>
                )}
              </span>
            </button>
          </li>
        )
      })}
    </ul>
  )
}

function placeholderPlan(recipeId: number, orderId: number | null): FullPlan {
  return {
    id: -Date.now(),
    display: '',
    multiplier: null,
    plannedAt: null,
    consumedAt: null,
    order: orderId == null ? 0 : null,
    orderPosition: orderId == null ? null : 0,
    orderId,
    recipeId,
    portionsEat: null,
    portionsFreezer: null,
    portionsConsumed: null,
    multiplierFreezer: null,
    recipe: null,
  }
}

function AddToOrder({
  recipeId,
  home,
  orders,
}: {
  recipeId: number
  home: SWRResponse<FullPlan[]>
  orders: SWRResponse<FullOrder[]>
}) {
  const [busy, setBusy] = useState<string | null>(null)
  const homePlans = home.data || []
  const orderList = orders.data || []
  const homePlan = homePlans.find((p) => p.recipeId === recipeId)

  async function toggleHome() {
    if (busy) return
    setBusy('home')
    const existing = homePlan
    const prev = homePlans

    if (existing) {
      void home.mutate(
        prev.filter((p) => p.id !== existing.id),
        { revalidate: false }
      )
      try {
        await api.delete('/api/plans/' + existing.id)
      } catch (e) {
        void home.mutate(prev, { revalidate: false })
        toast.error(e instanceof Error ? e.message : 'Mislukt')
      } finally {
        setBusy(null)
      }
      return
    }

    const temp = placeholderPlan(recipeId, null)
    void home.mutate([...prev, temp], { revalidate: false })
    try {
      const { data } = await api.post<FullPlan[]>('/api/plans', {
        recipeId,
        orderId: -1,
      })
      const created = Array.isArray(data) ? data[0] : data
      void home.mutate(
        (current) => {
          const base = (current || prev).filter((p) => p.id !== temp.id)
          return created ? [...base, created as FullPlan] : base
        },
        { revalidate: false }
      )
    } catch (e) {
      void home.mutate(prev, { revalidate: false })
      toast.error(e instanceof Error ? e.message : 'Mislukt')
    } finally {
      setBusy(null)
    }
  }

  async function toggleOrder(order: FullOrder) {
    const key = String(order.id)
    if (busy) return
    setBusy(key)
    const existing = order.plans.find((p) => p.recipeId === recipeId)
    const prev = orderList

    if (existing) {
      void orders.mutate(
        prev.map((o) =>
          o.id === order.id
            ? { ...o, plans: o.plans.filter((p) => p.id !== existing.id) }
            : o
        ),
        { revalidate: false }
      )
      try {
        await api.delete('/api/plans/' + existing.id)
      } catch (e) {
        void orders.mutate(prev, { revalidate: false })
        toast.error(e instanceof Error ? e.message : 'Mislukt')
      } finally {
        setBusy(null)
      }
      return
    }

    const temp = placeholderPlan(recipeId, order.id)
    void orders.mutate(
      prev.map((o) =>
        o.id === order.id ? { ...o, plans: [...o.plans, temp] } : o
      ),
      { revalidate: false }
    )
    try {
      const { data } = await api.post<FullPlan[]>('/api/plans', {
        recipeId,
        orderId: order.id,
      })
      const created = Array.isArray(data) ? data[0] : data
      void orders.mutate(
        (current) =>
          (current || prev).map((o) => {
            if (o.id !== order.id) return o
            const plans = o.plans.filter((p) => p.id !== temp.id)
            return {
              ...o,
              plans: created ? [...plans, created as FullPlan] : plans,
            }
          }),
        { revalidate: false }
      )
    } catch (e) {
      void orders.mutate(prev, { revalidate: false })
      toast.error(e instanceof Error ? e.message : 'Mislukt')
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-black/10">
      <span className="text-sm opacity-60 w-full mb-1">Planning</span>
      <button
        type="button"
        aria-busy={busy === 'home'}
        className={
          (homePlan ? 'bg-lime-600 text-white' : 'bg-slate-100 text-black') +
          ' rounded-full text-sm px-3 py-1.5 font-medium transition-colors' +
          (busy ? ' pointer-events-none' : '')
        }
        onClick={() => void toggleHome()}
      >
        {homePlan ? '✓ In huis' : '+ In huis'}
      </button>
      {orderList.map((order) => {
        const onOrder = order.plans.some((p) => p.recipeId === recipeId)
        const key = String(order.id)
        return (
          <button
            type="button"
            key={order.id}
            aria-busy={busy === key}
            className={
              (onOrder ? 'bg-lime-600 text-white' : 'bg-slate-100 text-black') +
              ' rounded-full text-sm px-3 py-1.5 font-medium transition-colors' +
              (busy ? ' pointer-events-none' : '')
            }
            onClick={() => void toggleOrder(order)}
          >
            {onOrder ? '✓' : '+'} {order.display || 'Bestelling'}
          </button>
        )
      })}
    </div>
  )
}

function AddToList({
  recipeId,
  lists,
  onChange,
}: {
  recipeId: number
  lists: FullList[]
  onChange: () => void
}) {
  const [creating, setCreating] = useState(false)
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)

  async function createAndAdd(display: string) {
    const trimmed = display.trim()
    if (!trimmed || busy) return
    setBusy(true)
    try {
      const res = await fetch('/api/lists', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          display: trimmed,
          accessLevel: 'everyone',
        }),
      })
      const data = await res.json()
      if (!res.ok) {
        toast.error(data.message || 'Mislukt')
        return
      }
      await fetch(`/api/lists/${data.id}/recipes`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipeId }),
      })
      setCreating(false)
      setName('')
      onChange()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Mislukt')
    } finally {
      setBusy(false)
    }
  }

  function cancelCreate() {
    setCreating(false)
    setName('')
  }

  return (
    <div className="flex flex-wrap gap-2 mt-6 pt-6 border-t border-black/10">
      <span className="text-sm opacity-60 w-full mb-1">Op lijst</span>
      {lists.map((list) => {
        const onList = list.recipes.some((r) => r.id === recipeId)
        return (
          <button
            type="button"
            key={list.id}
            className={
              (onList ? 'bg-lime-600 text-white' : 'bg-slate-100 text-black') +
              ' rounded-full text-sm px-3 py-1.5 font-medium transition-colors'
            }
            onClick={async () => {
              if (onList) {
                await fetch(`/api/lists/${list.id}/recipes/${recipeId}`, {
                  method: 'DELETE',
                  credentials: 'include',
                })
              } else {
                await fetch(`/api/lists/${list.id}/recipes`, {
                  method: 'POST',
                  credentials: 'include',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ recipeId }),
                })
              }
              onChange()
            }}
          >
            {onList ? '✓ ' : '+ '}
            {list.display}
          </button>
        )
      })}
      {creating ? (
        <input
          autoFocus
          disabled={busy}
          value={name}
          placeholder="Naam"
          aria-label="Nieuwe lijst"
          className="h-8 w-36 rounded-full px-3 text-sm font-medium leading-none bg-white text-black ring-1 ring-black/20 outline-none"
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              void createAndAdd(name)
            }
            if (e.key === 'Escape') cancelCreate()
          }}
          onBlur={() => {
            if (!name.trim()) {
              cancelCreate()
              return
            }
            void createAndAdd(name)
          }}
        />
      ) : (
        <button
          type="button"
          className="h-8 rounded-full px-3 text-sm font-medium leading-none bg-transparent text-black/45 ring-1 ring-dashed ring-black/20 hover:bg-slate-100 hover:text-black transition-colors"
          onClick={() => setCreating(true)}
        >
          + Nieuwe lijst
        </button>
      )}
    </div>
  )
}
